import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCampaignManagement } from "@/hooks/useCampaignManagement";
// TODO: Update this component to use the new transport system instead of listing.delivery
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import {
  ArrowRight,
  Gavel,
  Info,
  ShoppingCart,
  CheckCircle,
  XCircle,
  Truck,
} from "lucide-react";
import { useCampaigns } from "@/hooks/useCampaigns";
import { useTest } from "@/hooks/useTest";
import { useListingManagement } from "@/hooks/useListingManagement";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { commonButtonLabels } from "@/i18n/labels/commonLabels";
import { VehicleTest } from "@/types/vehicleTest";
import {
  ListingOverview,
  CreateListingRequest,
  ListingLeadbox,
} from "@/types/listing";
import {
  getTypeColor,
  getStatusColor,
  getCampaignStatus,
} from "@/utils/listingUtils";
import { Campaign } from "@/types/campaign";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { VehicleSelectionStep } from "./VehicleSelectionStep";
import {
  createUpdateListingRequest,
  createCreateListingRequest,
  ListingFormData,
  CreateListingFormData,
} from "@/selectors/listingSelectors";

interface CreateListingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preSelectedVehicle?: VehicleTest | null;
  preSelectedCampaign?: Campaign; // Campaign object
  preSelectedAdType?: "auction" | "direct-purchase"; // Campaign type
  existingListing?: ListingLeadbox; // Existing listing data for editing
  isEditMode?: boolean; // Whether this is edit mode
  onSuccess?: () => void;
}

export const CreateListingDialog = ({
  open,
  onOpenChange,
  preSelectedVehicle,
  preSelectedCampaign,
  preSelectedAdType,
  existingListing,
  isEditMode = false,
  onSuccess,
}: CreateListingDialogProps) => {
  const { isNative } = useNativePlatform();
  const navigate = useNavigate();
  const [step, setStep] = useState<"vehicle" | "type" | "campaign" | "rules">(
    "vehicle",
  );
  const prevOpenRef = useRef(false);
  const hasUserEditedDatesRef = useRef(false);
  const initializedCampaignDatesRef = useRef<string | null>(null);
  const [adType, setAdType] = useState<"auction" | "direct-purchase" | null>(
    null,
  );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [hideRatingIntervals, setHideRatingIntervals] = useState(false);
  const [showBidHistory, setShowBidHistory] = useState(false);
  const [campaignId, setCampaignId] = useState<string>("");
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleTest | null>(
    null,
  );
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Auction specific fields
  const [startingPrice, setStartingPrice] = useState("");
  const [reservePrice, setReservePrice] = useState("");
  const [minimumBidIncrement, setMinimumBidIncrement] = useState("");
  const [bidTimeBuffer, setBidTimeBuffer] = useState("40");
  const [isParallelAuction, setIsParallelAuction] = useState(false);
  const [pricingCategory, setPricingCategory] = useState<
    "standard" | "premium"
  >("standard");

  // Delivery fee zones
  const [deliveryFeeZoneA, setDeliveryFeeZoneA] = useState("");
  const [deliveryFeeZoneB, setDeliveryFeeZoneB] = useState("");
  const [deliveryFeeZoneC, setDeliveryFeeZoneC] = useState("");
  const [showDeliveryZones, setShowDeliveryZones] = useState(false);

  // Special pricing fields (for parallel auctions)
  const [enableSpecialPricing, setEnableSpecialPricing] = useState(false);
  const [specialStartingPrice, setSpecialStartingPrice] = useState("");
  const [autoRenewIfReserveNotReached, setAutoRenewIfReserveNotReached] =
    useState(false);
  const [autoRenewDays, setAutoRenewDays] = useState(7);

  // Direct purchase specific fields
  const [directPrice, setDirectPrice] = useState("");
  const [enableReducedDirectPrice, setEnableReducedDirectPrice] =
    useState(false);
  const [originalDirectPrice, setOriginalDirectPrice] = useState("");
  const [enableDirectPurchase, setEnableDirectPurchase] = useState(false);
  const [directPurchasePercentage, setDirectPurchasePercentage] =
    useState("10");
  const [directPurchaseFee, setDirectPurchaseFee] = useState("");
  const [directPurchaseType, setDirectPurchaseType] = useState<
    "percentage" | "fixed"
  >("percentage");
  const [directPurchaseFixedPrice, setDirectPurchaseFixedPrice] = useState("");

  // Direct purchase delivery fee zones
  const [directPurchaseDeliveryFeeZoneA, setDirectPurchaseDeliveryFeeZoneA] =
    useState("");
  const [directPurchaseDeliveryFeeZoneB, setDirectPurchaseDeliveryFeeZoneB] =
    useState("");
  const [directPurchaseDeliveryFeeZoneC, setDirectPurchaseDeliveryFeeZoneC] =
    useState("");
  const [showDirectPurchaseDeliveryZones, setShowDirectPurchaseDeliveryZones] =
    useState(false);

  // Date/time fields for listing
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExtendCampaignConfirm, setShowExtendCampaignConfirm] =
    useState(false);

  // State for tracking original values in edit mode
  const [originalValues, setOriginalValues] = useState({
    name: "",
    description: "",
    adType: null as "auction" | "direct-purchase" | null,
    startingPrice: "",
    reservePrice: "",
    minimumBidIncrement: "",
    bidTimeBuffer: "40",
    directPrice: "",
    startTime: "",
    endTime: "",
    deliveryFeeZoneA: "",
    deliveryFeeZoneB: "",
    deliveryFeeZoneC: "",
    directPurchaseDeliveryFeeZoneA: "",
    directPurchaseDeliveryFeeZoneB: "",
    directPurchaseDeliveryFeeZoneC: "",
    autoRenewIfReserveNotReached: false,
    autoRenewDays: 7,
    enableDirectPurchase: false,
    directPurchaseType: "percentage" as "percentage" | "fixed",
    directPurchasePercentage: "10",
    directPurchaseFixedPrice: "",
  });

  // Check if any changes have been made in edit mode
  const hasChanges = () => {
    if (!isEditMode) return true; // Always allow submission in create mode

    return (
      name !== originalValues.name ||
      description !== originalValues.description ||
      adType !== originalValues.adType ||
      startingPrice !== originalValues.startingPrice ||
      reservePrice !== originalValues.reservePrice ||
      minimumBidIncrement !== originalValues.minimumBidIncrement ||
      bidTimeBuffer !== originalValues.bidTimeBuffer ||
      directPrice !== originalValues.directPrice ||
      deliveryFeeZoneA !== originalValues.deliveryFeeZoneA ||
      deliveryFeeZoneB !== originalValues.deliveryFeeZoneB ||
      deliveryFeeZoneC !== originalValues.deliveryFeeZoneC ||
      directPurchaseDeliveryFeeZoneA !==
        originalValues.directPurchaseDeliveryFeeZoneA ||
      directPurchaseDeliveryFeeZoneB !==
        originalValues.directPurchaseDeliveryFeeZoneB ||
      directPurchaseDeliveryFeeZoneC !==
        originalValues.directPurchaseDeliveryFeeZoneC ||
      autoRenewIfReserveNotReached !==
        originalValues.autoRenewIfReserveNotReached ||
      autoRenewDays !== originalValues.autoRenewDays ||
      enableDirectPurchase !== originalValues.enableDirectPurchase ||
      directPurchaseType !== originalValues.directPurchaseType ||
      directPurchasePercentage !== originalValues.directPurchasePercentage ||
      directPurchaseFixedPrice !== originalValues.directPurchaseFixedPrice
    );
  };

  const { campaigns } = useCampaignManagement(1, 10);
  const queryClient = useQueryClient();
  const { language } = useLanguage();
  const L = (adminLabels(language) as any)?.leadboxListings ?? {};
  const btn = commonButtonLabels(language);
  const formatCampaignDateTime = (value: string) =>
    new Date(value).toLocaleString(language === "en" ? "en-CA" : "sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  const { adminTests, isLoadingAdminTests, totalAdminTests } = useTest();
  const { createListingAsync, updateListing } = useListingManagement();

  const handleStartTimeChange = useCallback((value: string) => {
    hasUserEditedDatesRef.current = true;
    setStartTime(value);
  }, []);

  const handleEndTimeChange = useCallback((value: string) => {
    hasUserEditedDatesRef.current = true;
    setEndTime(value);
  }, []);

  const resetForm = useCallback(() => {
    hasUserEditedDatesRef.current = false;
    initializedCampaignDatesRef.current = null;

    // If we have existing listing data, just reset form fields but don't set step
    // (step is handled by the useEffect above)
    if (existingListing) {
      setAdType(existingListing.listingType);
      setCampaignId(existingListing.campaignId || "");
      setName(existingListing.title || "");
      setDescription(existingListing.description || "");
      setHideRatingIntervals(existingListing.hideRatingIntervals === true);
      setShowBidHistory(existingListing.showBidHistory === true);

      // Handle auction-specific fields from auctionDetails
      if (existingListing.auctionDetails) {
        setStartingPrice(
          (existingListing.auctionDetails.startingPrice || 0).toString(),
        );
        // reservationPrice is mapped from reservePrice in backend - check both
        const reservePriceValue =
          (existingListing.auctionDetails as any).reservationPrice ??
          (existingListing.auctionDetails as any).reservePrice ??
          0;
        setReservePrice(reservePriceValue.toString());
        setMinimumBidIncrement(
          (existingListing.auctionDetails.minimumBidIncrement || 0).toString(),
        );
        setBidTimeBuffer(
          (existingListing.auctionDetails.bidTimeBuffer ?? 40).toString(),
        );

        const buyNowFixed = existingListing.auctionDetails.buyNowFixed;
        const buyNowPercentage =
          existingListing.auctionDetails.buyNowPercentage;
        const hasBuyNowEnabled =
          buyNowFixed != null || buyNowPercentage != null;
        const buyNowPercentageValue =
          buyNowPercentage != null
            ? (Number(buyNowPercentage) * 100).toString()
            : "10";

        setEnableDirectPurchase(hasBuyNowEnabled);
        setDirectPurchaseType(
          buyNowPercentage != null ? "percentage" : "fixed",
        );
        setDirectPurchasePercentage(buyNowPercentageValue);
        setDirectPurchaseFixedPrice(
          buyNowFixed != null ? buyNowFixed.toString() : "",
        );
      } else {
        setBidTimeBuffer("40");
        setEnableDirectPurchase(false);
        setDirectPurchaseType("percentage");
        setDirectPurchasePercentage("10");
        setDirectPurchaseFixedPrice("");
      }
      setAutoRenewIfReserveNotReached(
        (
          existingListing.auctionDetails as {
            autoRenewIfReserveNotReached?: boolean;
          }
        )?.autoRenewIfReserveNotReached ?? false,
      );
      setAutoRenewDays(
        (existingListing.auctionDetails as { autoRenewDays?: number | null })
          ?.autoRenewDays ?? 7,
      );
      // Handle delivery fees
      if (existingListing.deliveryFee) {
        const fees = existingListing.deliveryFee;
        // For direct purchase listings, populate direct purchase delivery fee zones
        if (existingListing.listingType === "direct-purchase") {
          if (fees.zoneA) {
            setDirectPurchaseDeliveryFeeZoneA(fees.zoneA.toString());
            setShowDirectPurchaseDeliveryZones(true);
          }
          if (fees.zoneB)
            setDirectPurchaseDeliveryFeeZoneB(fees.zoneB.toString());
          if (fees.zoneC)
            setDirectPurchaseDeliveryFeeZoneC(fees.zoneC.toString());
        } else {
          // For auction listings, populate auction delivery fee zones
          if (fees.zoneA) {
            setDeliveryFeeZoneA(fees.zoneA.toString());
            setShowDeliveryZones(true);
          }
          if (fees.zoneB) setDeliveryFeeZoneB(fees.zoneB.toString());
          if (fees.zoneC) setDeliveryFeeZoneC(fees.zoneC.toString());
        }
      }

      // Save original values for change detection
      setOriginalValues({
        enableDirectPurchase:
          !!existingListing.auctionDetails &&
          (existingListing.auctionDetails.buyNowFixed != null ||
            existingListing.auctionDetails.buyNowPercentage != null),
        directPurchaseType:
          existingListing.auctionDetails?.buyNowPercentage != null
            ? "percentage"
            : existingListing.auctionDetails?.buyNowFixed != null
              ? "fixed"
              : "percentage",
        directPurchasePercentage:
          existingListing.auctionDetails?.buyNowPercentage != null
            ? (
                Number(existingListing.auctionDetails.buyNowPercentage) * 100
              ).toString()
            : "10",
        directPurchaseFixedPrice:
          existingListing.auctionDetails?.buyNowFixed?.toString() || "",
        name: existingListing.title || "",
        description: existingListing.description || "",
        adType: existingListing.listingType || null,
        startingPrice:
          existingListing.auctionDetails?.startingPrice?.toString() || "",
        reservePrice:
          (
            (existingListing.auctionDetails as any)?.reservationPrice ??
            (existingListing.auctionDetails as any)?.reservePrice
          )?.toString() || "",
        minimumBidIncrement:
          existingListing.auctionDetails?.minimumBidIncrement?.toString() || "",
        bidTimeBuffer:
          existingListing.auctionDetails?.bidTimeBuffer?.toString() || "40",
        directPrice:
          existingListing.directPurchaseDetails?.currentPrice?.toString() || "",
        startTime: existingListing.startTime
          ? new Date(existingListing.startTime).toISOString()
          : "",
        endTime: (() => {
          const scheduled = existingListing.endTime
            ? new Date(existingListing.endTime)
            : null;
          const actual = existingListing.actualEndTime
            ? new Date(existingListing.actualEndTime)
            : null;
          const effective =
            actual && scheduled && actual > scheduled
              ? actual
              : scheduled;
          return effective ? effective.toISOString() : "";
        })(),
        deliveryFeeZoneA: existingListing.deliveryFee?.zoneA?.toString() || "",
        deliveryFeeZoneB: existingListing.deliveryFee?.zoneB?.toString() || "",
        deliveryFeeZoneC: existingListing.deliveryFee?.zoneC?.toString() || "",
        directPurchaseDeliveryFeeZoneA:
          existingListing.deliveryFee?.zoneA?.toString() || "",
        directPurchaseDeliveryFeeZoneB:
          existingListing.deliveryFee?.zoneB?.toString() || "",
        directPurchaseDeliveryFeeZoneC:
          existingListing.deliveryFee?.zoneC?.toString() || "",
        autoRenewIfReserveNotReached:
          (
            existingListing.auctionDetails as {
              autoRenewIfReserveNotReached?: boolean;
            }
          )?.autoRenewIfReserveNotReached ?? false,
        autoRenewDays:
          (existingListing.auctionDetails as { autoRenewDays?: number | null })
            ?.autoRenewDays ?? 7,
      });

      return;
    }

    // Otherwise, use the normal logic for pre-selected values
    if (preSelectedCampaign && preSelectedAdType) {
      // Start at vehicle selection step when campaign and adType are pre-selected
      // User still needs to select a vehicle, then will skip to rules step
      setStep("vehicle");
      setAdType(preSelectedAdType);
      setCampaignId(preSelectedCampaign._id);
    } else if (preSelectedVehicle) {
      setStep("type");
    } else {
      setStep("vehicle");
    }

    setAdType(preSelectedAdType || null);
    setName("");
    setDescription("");
    setHideRatingIntervals(false);
    setCampaignId(preSelectedCampaign?._id || "");
    setSelectedVehicle(preSelectedVehicle || null);
    setVehicleSearchQuery("");
    setCurrentPage(1);
    setStartingPrice("");
    setReservePrice("");
    setMinimumBidIncrement("");
    setBidTimeBuffer("40");
    setIsParallelAuction(false);
    setPricingCategory("standard");

    // Reset delivery fee zones
    setDeliveryFeeZoneA("");
    setDeliveryFeeZoneB("");
    setDeliveryFeeZoneC("");
    setShowDeliveryZones(false);

    setDirectPrice("");
    setEnableReducedDirectPrice(false);
    setOriginalDirectPrice("");
    setEnableDirectPurchase(false);
    setDirectPurchasePercentage("10");
    setDirectPurchaseType("percentage");
    setDirectPurchaseFixedPrice("");
    setDirectPurchaseFee("");

    // Reset direct purchase delivery fee zones
    setDirectPurchaseDeliveryFeeZoneA("");
    setDirectPurchaseDeliveryFeeZoneB("");
    setDirectPurchaseDeliveryFeeZoneC("");
    setShowDirectPurchaseDeliveryZones(false);

    setEnableSpecialPricing(false);
    setSpecialStartingPrice("");
    setAutoRenewIfReserveNotReached(false);

    // Reset date/time fields
    setStartTime("");
    setEndTime("");
  }, [
    existingListing,
    preSelectedCampaign,
    preSelectedAdType,
    preSelectedVehicle,
  ]);

  // Reset form only when dialog goes from closed to open (not when already open)
  useEffect(() => {
    // Only reset if dialog was closed before and is now opening
    if (open && !prevOpenRef.current) {
      resetForm();
    }
    prevOpenRef.current = open;
  }, [open, resetForm]);

  // Handle pre-selected vehicle
  useEffect(() => {
    if (preSelectedVehicle && open) {
      setSelectedVehicle(preSelectedVehicle);
      setStep("type"); // Skip vehicle selection step
    }
  }, [preSelectedVehicle, open]);

  // Handle pre-selected campaign and ad type
  useEffect(() => {
    if (preSelectedCampaign && preSelectedAdType && open) {
      setCampaignId(preSelectedCampaign._id);
      setAdType(preSelectedAdType);
      // Don't set step here - let resetForm handle it (starts at "vehicle" step)
    }
  }, [preSelectedCampaign, preSelectedAdType, open]);

  // Auto-populate price from selected vehicle when campaign and ad type are pre-selected
  useEffect(() => {
    if (preSelectedCampaign && preSelectedAdType && selectedVehicle && open) {
      // Auto-populate reserve price for auctions (customer's requested price)
      if (preSelectedAdType === "auction") {
        setReservePrice(selectedVehicle.price || "0");
      }
      // Auto-populate direct price for direct purchases
      if (preSelectedAdType === "direct-purchase") {
        setDirectPrice(selectedVehicle.price || "0");
      }
    }
  }, [preSelectedCampaign, preSelectedAdType, selectedVehicle, open]);

  // Handle existing listing data for editing
  useEffect(() => {
    if (existingListing && open) {
      // Pre-populate form with existing listing data
      setName(existingListing.title || "");
      setDescription(existingListing.description || "");

      if (existingListing.campaignId) {
        setCampaignId(existingListing.campaignId);
        setAdType(existingListing.listingType);
        // For regular listings with campaign, start at rules step
        setStep("rules");
      } else if (existingListing.status === "without-campaign") {
        // For without-campaign listings, start at campaign selection step
        setAdType(existingListing.listingType);
        setStep("campaign");
      } else {
        // Fallback to rules step
        setStep("rules");
      }

      // Handle auction-specific fields from auctionDetails
      if (existingListing.auctionDetails) {
        setStartingPrice(
          (existingListing.auctionDetails.startingPrice || 0).toString(),
        );
        // reservationPrice is mapped from reservePrice in backend - check both
        const reservePriceValue =
          (existingListing.auctionDetails as any).reservationPrice ??
          (existingListing.auctionDetails as any).reservePrice ??
          0;
        setReservePrice(reservePriceValue.toString());
        setMinimumBidIncrement(
          (existingListing.auctionDetails.minimumBidIncrement || 0).toString(),
        );
        setBidTimeBuffer(
          (existingListing.auctionDetails.bidTimeBuffer ?? 40).toString(),
        );

        const buyNowFixed = existingListing.auctionDetails.buyNowFixed;
        const buyNowPercentage =
          existingListing.auctionDetails.buyNowPercentage;
        const hasBuyNowEnabled =
          buyNowFixed != null || buyNowPercentage != null;
        const buyNowPercentageValue =
          buyNowPercentage != null
            ? (Number(buyNowPercentage) * 100).toString()
            : "10";

        setEnableDirectPurchase(hasBuyNowEnabled);
        setDirectPurchaseType(
          buyNowPercentage != null ? "percentage" : "fixed",
        );
        setDirectPurchasePercentage(buyNowPercentageValue);
        setDirectPurchaseFixedPrice(
          buyNowFixed != null ? buyNowFixed.toString() : "",
        );
      } else {
        setBidTimeBuffer("40");
        setEnableDirectPurchase(false);
        setDirectPurchaseType("percentage");
        setDirectPurchasePercentage("10");
        setDirectPurchaseFixedPrice("");
      }

      // Handle direct purchase price (+ optional sänkt pris)
      if (existingListing.directPurchaseDetails) {
        const dp = existingListing.directPurchaseDetails;
        setDirectPrice(dp.currentPrice.toString());
        const orig = dp.originalPrice;
        if (orig != null && orig > dp.currentPrice) {
          setEnableReducedDirectPrice(true);
          setOriginalDirectPrice(orig.toString());
        } else {
          setEnableReducedDirectPrice(false);
          setOriginalDirectPrice("");
        }
      }

      // Handle delivery fees
      if (existingListing.deliveryFee) {
        const fees = existingListing.deliveryFee;
        // For direct purchase listings, populate direct purchase delivery fee zones
        if (existingListing.listingType === "direct-purchase") {
          if (fees.zoneA) {
            setDirectPurchaseDeliveryFeeZoneA(fees.zoneA.toString());
            setShowDirectPurchaseDeliveryZones(true);
          }
          if (fees.zoneB)
            setDirectPurchaseDeliveryFeeZoneB(fees.zoneB.toString());
          if (fees.zoneC)
            setDirectPurchaseDeliveryFeeZoneC(fees.zoneC.toString());
        } else {
          // For auction listings, populate auction delivery fee zones
          if (fees.zoneA) {
            setDeliveryFeeZoneA(fees.zoneA.toString());
            setShowDeliveryZones(true);
          }
          if (fees.zoneB) setDeliveryFeeZoneB(fees.zoneB.toString());
          if (fees.zoneC) setDeliveryFeeZoneC(fees.zoneC.toString());
        }
      }

      // Handle startTime and endTime (prefer anti-snipe actualEndTime when later)
      if (existingListing.startTime) {
        setStartTime(new Date(existingListing.startTime).toISOString());
      }
      if (existingListing.endTime || existingListing.actualEndTime) {
        const scheduled = existingListing.endTime
          ? new Date(existingListing.endTime)
          : null;
        const actual = existingListing.actualEndTime
          ? new Date(existingListing.actualEndTime)
          : null;
        const effective =
          actual && scheduled && actual > scheduled
            ? actual
            : scheduled || actual;
        if (effective) setEndTime(effective.toISOString());
      }
    }
  }, [existingListing, open]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [vehicleSearchQuery]);

  // Filter approved tests TODO: Fixa ett sätt att hämta bara dessa direkt i anropet? Så vi inte behöver hämta alla och sen filtrear här?
  const approvedTests = adminTests.filter((test) => test.status === "approved");

  // Filter tests based on search query
  const filteredTests = approvedTests.filter(
    (test) =>
      test.vehicleDetails.regNumber
        .toLowerCase()
        .includes(vehicleSearchQuery.toLowerCase()) ||
      test.vehicleName.toLowerCase().includes(vehicleSearchQuery.toLowerCase()),
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredTests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTests = filteredTests.slice(startIndex, endIndex);

  // Prefer list lookup; fall back to preSelectedCampaign (dealer flow may not be in page-1)
  const selectedCampaign =
    campaigns.find((c) => c._id === campaignId) ??
    (preSelectedCampaign && preSelectedCampaign._id === campaignId
      ? preSelectedCampaign
      : undefined);

  // Initialize startTime and endTime from campaign when campaign is selected
  useEffect(() => {
    if (
      open &&
      selectedCampaign &&
      campaignId &&
      !startTime &&
      !endTime &&
      !hasUserEditedDatesRef.current &&
      initializedCampaignDatesRef.current !== selectedCampaign._id
    ) {
      // Initialize with campaign dates if not already set
      setStartTime(new Date(selectedCampaign.startDate).toISOString());
      setEndTime(new Date(selectedCampaign.endDate).toISOString());
      initializedCampaignDatesRef.current = selectedCampaign._id;
    }
  }, [selectedCampaign, campaignId, open, startTime, endTime]);

  // Filter campaigns based on selected ad type and status (only active and upcoming, exclude cancelled)
  const filteredCampaigns = campaigns?.filter((campaign) => {
    // First filter by ad type
    if (adType && campaign.type !== adType) return false;

    // Get campaign status (handles both explicit status and calculated status from dates)
    const campaignStatus = getCampaignStatus(campaign);

    // Only show active and upcoming campaigns, exclude cancelled and completed
    return campaignStatus === "active" || campaignStatus === "upcoming";
  });

  const hasBidsInEditAuction =
    isEditMode &&
    adType === "auction" &&
    (existingListing?.bidding?.totalBids || 0) > 0;
  const isBuyNowActivationBlockedInEdit = hasBidsInEditAuction;

  const handleVehicleSelect = (test: VehicleTest) => {
    setSelectedVehicle(test);
    // If campaign and adType are pre-selected, skip type and campaign steps
    if (preSelectedCampaign && preSelectedAdType) {
      // Auto-populate price from selected vehicle
      if (preSelectedAdType === "auction") {
        setReservePrice(test.price || "0");
      } else if (preSelectedAdType === "direct-purchase") {
        setDirectPrice(test.price || "0");
      }
      setStep("rules");
    } else {
      setStep("type");
    }
  };

  const handleTypeSelect = (type: "auction" | "direct-purchase") => {
    setAdType(type);
    setStep("campaign");
  };

  const handleCampaignSelect = (campaignId: string) => {
    setCampaignId(campaignId);
    const campaign = campaigns.find((c) => c._id === campaignId);
    if (campaign?.startDate && campaign?.endDate) {
      // Explicit campaign selection should reset dates to the campaign range.
      hasUserEditedDatesRef.current = false;
      setStartTime(new Date(campaign.startDate).toISOString());
      setEndTime(new Date(campaign.endDate).toISOString());
      initializedCampaignDatesRef.current = campaign._id;
    }
    if (campaign && selectedVehicle) {
      // Auto-populate reserve price for auctions (customer's requested price)
      if (adType === "auction") {
        setReservePrice(selectedVehicle.price || "0");
      }
      // Auto-populate direct price for direct purchases
      if (adType === "direct-purchase") {
        setDirectPrice(selectedVehicle.price || "0");
      }
    }
    setStep("rules");
  };

  const handleBack = () => {
    if (step === "rules") {
      // If campaign and adType are pre-selected, go back to vehicle selection
      if (preSelectedCampaign && preSelectedAdType) {
        setStep("vehicle");
      } else {
        setStep("campaign");
      }
    } else if (step === "campaign") {
      // If we have pre-selected campaign and ad type, this step shouldn't be reached
      // but if it is, go back to vehicle
      if (preSelectedCampaign && preSelectedAdType) {
        setStep("vehicle");
      } else {
        setStep("type");
      }
    } else if (step === "type") {
      // If we have a pre-selected vehicle, close the dialog instead of going back to vehicle selection
      if (preSelectedVehicle) {
        onOpenChange(false);
      } else {
        setStep("vehicle");
      }
    }
  };

  const handleSubmit = async () => {
    // In edit mode, we don't need to validate all required fields since they already exist
    if (isEditMode && existingListing) {
      // For edit mode, just check if we have the basic data
      if (!adType || !name || !description) {
        toast.error(L.basicFieldsRequired);
        return;
      }
    } else {
      if (
        !adType ||
        !name ||
        !description ||
        !campaignId ||
        !selectedVehicle
        // !selectedVehicle.images TODO: Temporary disabled until backend is integrated
      ) {
        toast.error(L.allFieldsRequired);
        return;
      }
    }

    // Validate auction-specific fields only if they're being used
    if (
      adType === "auction" &&
      (!startingPrice ||
        !minimumBidIncrement ||
        !bidTimeBuffer ||
        !reservePrice ||
        !deliveryFeeZoneA)
    ) {
      toast.error(L.auctionFieldsRequired);
      return;
    }

    // Validate reservationPrice >= startingPrice for auctions
    if (adType === "auction" && startingPrice && reservePrice) {
      const startPrice = parseFloat(startingPrice);
      const reservePriceNum = parseFloat(reservePrice);
      if (reservePriceNum < startPrice) {
        toast.error(L.reservePriceMustBeGte);
        return;
      }
    }

    if (adType === "auction") {
      const bidTimeBufferValue = Number.parseInt(bidTimeBuffer, 10);
      if (Number.isNaN(bidTimeBufferValue) || bidTimeBufferValue < 0) {
        toast.error(L.bidTimeBufferValidation);
        return;
      }
    }

    // Validate direct purchase fields only if they're being used
    if (
      adType === "direct-purchase" &&
      (!directPrice || !directPurchaseDeliveryFeeZoneA)
    ) {
      toast.error(L.directPriceAndDeliveryRequired);
      return;
    }

    if (adType === "direct-purchase" && enableReducedDirectPrice) {
      const current = parseFloat(directPrice);
      const original = parseFloat(originalDirectPrice);
      if (
        !originalDirectPrice ||
        Number.isNaN(original) ||
        Number.isNaN(current) ||
        original <= current
      ) {
        toast.error(
          L.reducedPriceValidation ??
            "Ursprungligt pris måste vara högre än det sänkta priset",
        );
        return;
      }
    }

    if (adType === "auction" && enableDirectPurchase) {
      if (directPurchaseType === "percentage" && !directPurchasePercentage) {
        toast.error(L.directPurchasePercentRequired);
        return;
      }
      if (directPurchaseType === "fixed" && !directPurchaseFixedPrice) {
        toast.error(L.directPurchaseFixedRequired);
        return;
      }
    }

    // Validate date/time fields
    if (!startTime || !endTime) {
      toast.error(L.startAndEndTimeRequired);
      return;
    }

    // Validate that endTime is after startTime
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    if (endDate <= startDate) {
      toast.error(L.endTimeAfterStart);
      return;
    }

    // Validate against campaign start; past campaign end requires confirm (extends campaign)
    if (selectedCampaign) {
      const campaignStart = new Date(selectedCampaign.startDate);
      const campaignEnd = new Date(selectedCampaign.endDate);
      const campaignStartStr = formatCampaignDateTime(
        selectedCampaign.startDate,
      );
      const campaignEndStr = formatCampaignDateTime(selectedCampaign.endDate);
      if (startDate < campaignStart) {
        toast.error(
          (
            L.timeWithinCampaign ??
            "Starttid måste vara inom kampanjens datumintervall ({start} - {end})"
          )
            .replace("{start}", campaignStartStr)
            .replace("{end}", campaignEndStr),
        );
        return;
      }

      if (endDate < campaignStart) {
        toast.error(
          (
            L.endTimeWithinCampaign ??
            "Sluttid måste vara inom kampanjens datumintervall ({start} - {end})"
          )
            .replace("{start}", campaignStartStr)
            .replace("{end}", campaignEndStr),
        );
        return;
      }

      if (endDate > campaignEnd) {
        setShowExtendCampaignConfirm(true);
        return;
      }
    }

    await persistListing(false);
  };

  const persistListing = async (extendedCampaign: boolean) => {
    setIsSubmitting(true);
    setShowExtendCampaignConfirm(false);

    try {
      if (isEditMode && existingListing) {
        const formData: ListingFormData = {
          id: existingListing.id,
          title: name,
          description,
          listingType: adType!,
          hideRatingIntervals,
          showBidHistory,
          startTime: startTime ? new Date(startTime) : undefined,
          endTime: endTime ? new Date(endTime) : undefined,
          startingPrice,
          reservePrice,
          minimumBidIncrement,
          bidTimeBuffer,
          enableSpecialPricing,
          specialStartingPrice,
          autoRenewIfReserveNotReached,
          autoRenewDays,
          enableDirectPurchase,
          directPurchaseType,
          directPurchasePercentage,
          directPurchaseFixedPrice,
          directPrice,
          enableReducedDirectPrice,
          originalDirectPrice,
          selectedVehiclePrice:
            selectedVehicle?.price ||
            existingListing.directPurchaseDetails?.originalPrice?.toString() ||
            "0",
          deliveryFeeZoneA,
          deliveryFeeZoneB,
          deliveryFeeZoneC,
          directPurchaseDeliveryFeeZoneA,
          directPurchaseDeliveryFeeZoneB,
          directPurchaseDeliveryFeeZoneC,
        };

        const updateData = createUpdateListingRequest(formData);
        await updateListing(updateData);

        toast.success(
          extendedCampaign
            ? (L.campaignAndListingUpdated ??
                "Annons uppdaterad och kampanjens slut förlängt.")
            : L.listingUpdated,
        );
      } else {
        const formData: CreateListingFormData = {
          title: name,
          description,
          listingType: adType!,
          hideRatingIntervals,
          showBidHistory,
          startingPrice,
          reservePrice,
          minimumBidIncrement,
          bidTimeBuffer,
          enableSpecialPricing,
          specialStartingPrice,
          autoRenewIfReserveNotReached,
          autoRenewDays,
          enableDirectPurchase,
          directPurchaseType,
          directPurchasePercentage,
          directPurchaseFixedPrice,
          directPrice,
          enableReducedDirectPrice,
          originalDirectPrice,
          selectedVehiclePrice: selectedVehicle?.price || "0",
          deliveryFeeZoneA,
          deliveryFeeZoneB,
          deliveryFeeZoneC,
          directPurchaseDeliveryFeeZoneA,
          directPurchaseDeliveryFeeZoneB,
          directPurchaseDeliveryFeeZoneC,
          campaignId,
          sellerId: selectedVehicle!.customerId,
          carTestId: selectedVehicle!.id,
          startTime: new Date(startTime),
          endTime: new Date(endTime),
        };

        const listingData = createCreateListingRequest(formData);
        await createListingAsync(listingData);

        toast.success(
          extendedCampaign
            ? (L.campaignAndListingUpdated ??
                "Annons uppdaterad och kampanjens slut förlängt.")
            : L.listingCreated,
          extendedCampaign
            ? undefined
            : { description: L.listingCreatedTransportNote },
        );
      }

      await queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      await queryClient.invalidateQueries({ queryKey: ["campaign"] });

      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      let errorMessage = isEditMode
        ? L.listingUpdateError
        : L.listingCreateError;

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.details?.error) {
        errorMessage = error.details.error;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to validate delivery fees
  const validateDeliveryFees = () => {
    if (adType === "auction") {
      return deliveryFeeZoneA && reservePrice;
    } else if (adType === "direct-purchase") {
      return directPurchaseDeliveryFeeZoneA;
    }
    return true;
  };

  const canProceed = () => {
    switch (step) {
      case "vehicle":
        return selectedVehicle !== null;
      case "type":
        return adType !== null;
      case "campaign":
        return campaignId !== "";
      case "rules":
        return (
          name &&
          description &&
          validateDeliveryFees() &&
          (adType === "auction"
            ? startingPrice && minimumBidIncrement && bidTimeBuffer
            : directPrice)
        );
      default:
        return false;
    }
  };

  const renderVehicleSelection = () => (
    <VehicleSelectionStep
      onVehicleSelect={handleVehicleSelect}
      isStandalone={false}
    />
  );

  const renderTypeSelection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{L.selectListingType}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {L.selectListingTypeFor?.replace(
            "{regNumber}",
            selectedVehicle?.vehicleDetails.regNumber ?? "",
          )}
        </p>
      </div>

      {selectedVehicle && (
        <Card className="p-4 mb-6">
          <h4 className="font-semibold mb-2">{L.chosenVehicle}</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">{L.regNr}:</span>
              <p className="font-medium">
                {selectedVehicle.vehicleDetails.regNumber}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">{L.vehicle}:</span>
              <p className="font-medium">{selectedVehicle.vehicleName}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{L.price}:</span>
              <p className="font-medium">{selectedVehicle.price} kr</p>
            </div>
            <div>
              <span className="text-muted-foreground">
                {L.statusVehicleTest}:{" "}
              </span>
              <Badge variant="default" className="bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                {L.approved}
              </Badge>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          className={`p-6 cursor-pointer transition-colors border border-orange-500 hover:bg-accent/20 ${
            adType === "auction" ? "ring-2 ring-primary" : ""
          }`}
          onClick={() => handleTypeSelect("auction")}
        >
          <div className="flex items-center gap-3 mb-4">
            <Gavel className="h-6 w-6 text-primary" />
            <h4 className="font-semibold">{L.auction ?? "Auktion"}</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            {L.auctionDesc ?? L.createAuctionDesc}
          </p>
        </Card>

        <Card
          className={`p-6 cursor-pointer transition-colors border border-green-500 hover:bg-accent/20 ${
            adType === "direct-purchase" ? "ring-2 ring-primary" : ""
          }`}
          onClick={() => handleTypeSelect("direct-purchase")}
        >
          <div className="flex items-center gap-3 mb-4">
            <ShoppingCart className="h-6 w-6 text-primary" />
            <h4 className="font-semibold">{L.directPurchase ?? "Direktköp"}</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            {L.directPurchaseDesc ??
              "Sätt ett fast pris för direktköp av fordonet"}
          </p>
        </Card>
      </div>
    </div>
  );

  const renderCampaignSelection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{L.selectCampaign}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {L.selectCampaignFor}{" "}
          {adType === "auction" ? L.auctionType : L.directPurchaseType}
        </p>
      </div>

      <div className="space-y-4">
        {selectedCampaign && (
          <Card className="p-4 border-2 border-blue-500">
            <h4 className="font-semibold text-blue-500 mb-2">
              {L.chosenCampaign}
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-500">{L.name ?? "Namn"}:</span>
                <p className="font-medium">{selectedCampaign.name}</p>
              </div>
              <div>
                <span className="text-blue-500">{L.type ?? "Typ"}: </span>
                <Badge
                  className={`text-xs ${getTypeColor(selectedCampaign.type)}`}
                >
                  {selectedCampaign.type === "auction"
                    ? (L.auction ?? "Auktion")
                    : (L.directPurchase ?? "Direktköp")}
                </Badge>
              </div>
              <div>
                <span className="text-blue-500">{L.status ?? "Status"}: </span>
                <Badge
                  className={`text-xs ${getStatusColor(
                    getCampaignStatus(selectedCampaign),
                  )}`}
                >
                  {getCampaignStatus(selectedCampaign) === "active"
                    ? (L.active ?? "Aktiv")
                    : (L.upcoming ?? "Kommande")}
                </Badge>
              </div>
              <div>
                <span className="text-blue-500">
                  {L.start ?? "Startdatum"}:
                </span>
                <p className="font-medium">
                  {formatCampaignDateTime(selectedCampaign.startDate)}
                </p>
              </div>
              <div>
                <span className="text-blue-500">{L.end ?? "Slutdatum"}:</span>
                <p className="font-medium">
                  {formatCampaignDateTime(selectedCampaign.endDate)}
                </p>
              </div>
            </div>
          </Card>
        )}
        {filteredCampaigns?.map((campaign) => (
          <Card
            key={campaign._id}
            className={`p-4 cursor-pointer transition-colors hover:bg-accent/20 ${
              campaignId === campaign._id ? "ring-2 ring-primary" : ""
            }`}
            onClick={() => handleCampaignSelect(campaign._id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-semibold">{campaign.name}</h4>
                  <Badge className={`text-xs ${getTypeColor(campaign.type)}`}>
                    {campaign.type === "auction"
                      ? (L.auction ?? "Auktion")
                      : (L.directPurchase ?? "Direktköp")}
                  </Badge>
                  <Badge
                    className={`text-xs ${getStatusColor(
                      getCampaignStatus(campaign),
                    )}`}
                  >
                    {getCampaignStatus(campaign) === "active"
                      ? (L.active ?? "Aktiv")
                      : (L.upcoming ?? "Kommande")}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {campaign.description}
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">
                      {L.start ?? "Start"}:
                    </span>
                    <p className="font-medium">
                      {formatCampaignDateTime(campaign.startDate)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      {L.end ?? "Slut"}:
                    </span>
                    <p className="font-medium">
                      {formatCampaignDateTime(campaign.endDate)}
                    </p>
                  </div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderRules = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{L.listingInfo}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {L.fillListingInfo}{" "}
          {adType === "auction" ? L.auctionType : L.directPurchaseType}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="name">{L.listingName}</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={L.listingNamePlaceholder}
          />
        </div>

        <div>
          <Label htmlFor="description">{L.description} *</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={L.describeListing}
            rows={3}
          />
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
          <div className="space-y-1">
            <Label htmlFor="hideRatingIntervals">Dölj prisintervall</Label>
            <p className="text-xs text-muted-foreground">
              Döljer AutoUncle-prisintervall på annonssidan. Visas som standard
              när data finns (direktköp döljs automatiskt om priset är över
              rimligt).
            </p>
          </div>
          <Switch
            id="hideRatingIntervals"
            checked={hideRatingIntervals}
            onCheckedChange={setHideRatingIntervals}
          />
        </div>

        {adType === "auction" ? (
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <div className="space-y-1">
              <Label htmlFor="showBidHistory">Visa budhistorik</Label>
              <p className="text-xs text-muted-foreground">
                Köpare får en statistikknapp i budrutan som visar anonym
                budhistorik (belopp och tid, utan namn).
              </p>
            </div>
            <Switch
              id="showBidHistory"
              checked={showBidHistory}
              onCheckedChange={setShowBidHistory}
            />
          </div>
        ) : null}

        {/* Date/Time Selection */}
        <div className="space-y-4">
          <h4 className="font-semibold">{L.timePeriod}</h4>
          <p className="text-sm text-muted-foreground">
            {selectedCampaign
              ? L.selectTimeInCampaign
              : L.selectTimeForListing}
          </p>
          {selectedCampaign && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-950/30">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="font-medium text-amber-950 dark:text-amber-100">
                  {L.campaignCeilingLabel ?? "Kampanjens tak (slut)"}:
                </span>
                <span className="font-semibold tabular-nums text-amber-900 dark:text-amber-50">
                  {formatCampaignDateTime(selectedCampaign.endDate)}
                </span>
              </div>
              <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-100/80">
                {L.campaignCeilingHint ??
                  "Annonsen kan förlängas fritt fram till detta datum. Väljer du senare tid förlängs kampanjen också (efter bekräftelse)."}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {formatCampaignDateTime(selectedCampaign.startDate)} –{" "}
                {formatCampaignDateTime(selectedCampaign.endDate)}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-4">
            <div>
              <DateTimePicker
                value={startTime}
                onChange={handleStartTimeChange}
                label={L.startTime}
                renderTimeExtra={
                  selectedCampaign &&
                  (() => {
                    const now = new Date();
                    const campaignEnd = new Date(selectedCampaign.endDate);
                    // Show Nu while campaign has not yet ended (active or just starting)
                    if (now > campaignEnd) return null;
                    return (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          handleStartTimeChange(now.toISOString());
                        }}
                      >
                        {L.now}
                      </Button>
                    );
                  })()
                }
              />
            </div>
            <DateTimePicker
              value={endTime}
              onChange={handleEndTimeChange}
              label={L.endTime}
            />
          </div>
        </div>

        {adType === "auction" && (
          <div className="space-y-4">
            <h4 className="font-semibold">{L.auctionRules}</h4>
            <p className="text-sm text-muted-foreground">
              {L.requiredFieldsNote}
            </p>

            <div className="space-y-4">
              {/*  // INFO: Disabled for now until we have a way to handle special prices 
              <div className="flex items-center space-x-2">
                <Switch
                  id="enableSpecialPricing"
                  checked={enableSpecialPricing}
                  onCheckedChange={setEnableSpecialPricing}
                />
                <Label htmlFor="enableSpecialPricing">Specialpris</Label>
              </div> */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startingPrice">{L.startingPrice}</Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    {L.startingPriceDesc}
                  </p>
                  <Input
                    id="startingPrice"
                    type="number"
                    value={startingPrice}
                    onChange={(e) => setStartingPrice(e.target.value)}
                    placeholder="0"
                  />
                </div>

                {enableSpecialPricing && (
                  <div>
                    <Label
                      htmlFor="specialStartingPrice"
                      className="text-blue-600"
                    >
                      {L.specialStartingPrice}
                    </Label>
                    <p className="text-xs text-blue-600/70 mb-1">
                      {L.specialStartingPriceDesc}
                    </p>
                    <Input
                      id="specialStartingPrice"
                      type="number"
                      value={specialStartingPrice}
                      onChange={(e) => setSpecialStartingPrice(e.target.value)}
                      placeholder="0"
                      className="border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="reservePrice">{L.reservePrice}</Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    {L.reservePriceDesc}
                  </p>
                  <Input
                    id="reservePrice"
                    type="number"
                    value={reservePrice}
                    onChange={(e) => setReservePrice(e.target.value)}
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label htmlFor="minimumBidIncrement">
                    {L.minimumBidIncrement}
                  </Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    {L.minimumBidIncrementDesc}
                  </p>
                  <Input
                    id="minimumBidIncrement"
                    type="number"
                    value={minimumBidIncrement}
                    onChange={(e) => setMinimumBidIncrement(e.target.value)}
                    placeholder="100"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="bidTimeBuffer">{L.bidTimeBuffer}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="focus:outline-none">
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="max-w-xs p-3">
                        <div className="space-y-2">
                          <p className="font-semibold text-sm">
                            {L.bidTimeBufferInfoTitle}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {L.bidTimeBufferInfo}
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {L.bidTimeBufferDesc}
                  </p>
                  <Input
                    id="bidTimeBuffer"
                    type="number"
                    min="0"
                    step="1"
                    value={bidTimeBuffer}
                    onChange={(e) => setBidTimeBuffer(e.target.value)}
                    placeholder="40"
                  />
                </div>

                <div className="rounded-lg border p-4 bg-muted/20 space-y-4 col-span-full">
                  <div className="flex items-center space-x-3">
                    <Switch
                      id="autoRenewIfReserveNotReached"
                      checked={autoRenewIfReserveNotReached}
                      onCheckedChange={setAutoRenewIfReserveNotReached}
                    />
                    <Label
                      htmlFor="autoRenewIfReserveNotReached"
                      className="cursor-pointer"
                    >
                      {L.autoRenewIfReserveNotReached}
                    </Label>
                  </div>
                  {autoRenewIfReserveNotReached && (
                    <div className="space-y-2 pt-1">
                      <Label htmlFor="autoRenewDays">{L.autoRenewDays}</Label>
                      <Select
                        value={autoRenewDays.toString()}
                        onValueChange={(v) => setAutoRenewDays(parseInt(v, 10))}
                      >
                        <SelectTrigger
                          id="autoRenewDays"
                          className="w-full max-w-[200px]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 20 }, (_, i) => i + 1).map(
                            (d) => (
                              <SelectItem key={d} value={d.toString()}>
                                {d}{" "}
                                {d === 1
                                  ? (L.autoRenewDaysDay ?? "dag")
                                  : (L.autoRenewDaysDays ?? "dagar")}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="deliveryFeeZoneA">{L.deliveryFeeZoneA}</Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    {L.deliveryFeeZoneADesc}
                  </p>
                  <Input
                    id="deliveryFeeZoneA"
                    type="number"
                    value={deliveryFeeZoneA}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDeliveryFeeZoneA(value);
                      if (value && !showDeliveryZones) {
                        setShowDeliveryZones(true);
                      }
                      // Auto-calculate other zones whenever zone A changes
                      if (value) {
                        const zoneA = parseFloat(value);
                        setDeliveryFeeZoneB((zoneA + 1000).toString());
                        setDeliveryFeeZoneC((zoneA + 2000).toString());
                      }
                    }}
                    placeholder="1900"
                  />
                </div>
              </div>

              {showDeliveryZones && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                  <h5 className="font-medium text-blue-600">
                    {L.deliveryFeesPerZone}
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="deliveryFeeZoneB">{L.zoneB}</Label>
                      <Input
                        id="deliveryFeeZoneB"
                        type="number"
                        value={deliveryFeeZoneB}
                        onChange={(e) => setDeliveryFeeZoneB(e.target.value)}
                        placeholder="2900"
                      />
                    </div>
                    <div>
                      <Label htmlFor="deliveryFeeZoneC">{L.zoneC}</Label>

                      <Input
                        id="deliveryFeeZoneC"
                        type="number"
                        value={deliveryFeeZoneC}
                        onChange={(e) => setDeliveryFeeZoneC(e.target.value)}
                        placeholder="3900"
                      />
                    </div>
                    <div>
                      <Label htmlFor="deliveryFeeZoneD">{L.zoneD}</Label>
                      <p className="mt-2 text-accent">{L.priceOnRequest}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="enableDirectPurchase"
                  checked={enableDirectPurchase}
                  disabled={
                    isEditMode &&
                    isBuyNowActivationBlockedInEdit &&
                    !enableDirectPurchase
                  }
                  onCheckedChange={setEnableDirectPurchase}
                />
                <Label htmlFor="enableDirectPurchase">
                  {L.enableDirectPurchase}
                </Label>
              </div>

              {isEditMode &&
                isBuyNowActivationBlockedInEdit &&
                !enableDirectPurchase && (
                  <p className="text-xs text-muted-foreground">
                    {L.buyNowActivationBlockedWithBids}
                  </p>
                )}

              {enableDirectPurchase && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <h5 className="font-medium">
                    {L.directPurchaseOptions ?? "Direktköps-alternativ"}
                  </h5>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="percentage"
                        name="directPurchaseType"
                        value="percentage"
                        checked={directPurchaseType === "percentage"}
                        onChange={(e) =>
                          setDirectPurchaseType(
                            e.target.value as "percentage" | "fixed",
                          )
                        }
                      />
                      <Label htmlFor="percentage">
                        {L.percentOverHighest ?? "Procentuellt över högsta bud"}
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="fixed"
                        name="directPurchaseType"
                        value="fixed"
                        checked={directPurchaseType === "fixed"}
                        onChange={(e) =>
                          setDirectPurchaseType(
                            e.target.value as "percentage" | "fixed",
                          )
                        }
                      />
                      <Label htmlFor="fixed">
                        {L.fixedPrice ?? "Fast pris"}
                      </Label>
                    </div>
                  </div>

                  {directPurchaseType === "percentage" && (
                    <div>
                      <Label htmlFor="directPurchasePercentage">
                        {L.priceIncreasePercent ?? "Prisökning (%) *"}
                      </Label>
                      <p className="text-xs text-muted-foreground mb-1">
                        {L.percentOverBidDesc ?? "Procent över högsta budet"}
                      </p>
                      <Input
                        id="directPurchasePercentage"
                        type="number"
                        min="1"
                        max="100"
                        value={directPurchasePercentage}
                        onChange={(e) =>
                          setDirectPurchasePercentage(e.target.value)
                        }
                        placeholder="10"
                      />
                    </div>
                  )}

                  {directPurchaseType === "fixed" && (
                    <div>
                      <Label htmlFor="directPurchaseFixedPrice">
                        {L.fixedDirectPurchasePrice ??
                          "Fast direktköpspris (kr) *"}
                      </Label>
                      <p className="text-xs text-muted-foreground mb-1">
                        {L.fixedDirectPriceDesc ?? "Fast pris för direktköp"}
                      </p>
                      <Input
                        id="directPurchaseFixedPrice"
                        type="number"
                        value={directPurchaseFixedPrice}
                        onChange={(e) =>
                          setDirectPurchaseFixedPrice(e.target.value)
                        }
                        placeholder="0"
                      />
                    </div>
                  )}

                  {/* 
                  // INFO: Disabled for now until we have a way to handle direct purchase fees
                  <div>
                    <Label htmlFor="directPurchaseFee">
                      {L.deliveryFeeZoneA ?? "Direktköpsavgift"} (kr)
                    </Label>
                    <p className="text-xs text-muted-foreground mb-1">
                      Extra avgift för direktköp (valfritt)
                    </p>
                    <Input
                      id="directPurchaseFee"
                      type="number"
                      value={directPurchaseFee}
                      onChange={(e) => setDirectPurchaseFee(e.target.value)}
                      placeholder="0"
                    />
                  </div> */}
                </div>
              )}
            </div>
          </div>
        )}

        {adType === "direct-purchase" && (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="enableReducedDirectPrice"
                checked={enableReducedDirectPrice}
                onCheckedChange={(checked) => {
                  setEnableReducedDirectPrice(checked);
                  if (!checked) setOriginalDirectPrice("");
                }}
              />
              <div>
                <Label htmlFor="enableReducedDirectPrice">
                  {L.enableReducedPrice ?? "Visa som sänkt pris"}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {L.enableReducedPriceDesc ??
                    "Använd när annonsen ska visas med överstruket ursprungligt pris (t.ex. omlagd direktköp)."}
                </p>
              </div>
            </div>

            {enableReducedDirectPrice && (
              <div>
                <Label htmlFor="originalDirectPrice">
                  {L.originalDirectPrice ?? "Ursprungligt pris *"}
                </Label>
                <p className="text-xs text-muted-foreground mb-1">
                  {L.originalDirectPriceDesc ??
                    "Priset innan sänkningen (visas överstruket)"}
                </p>
                <Input
                  id="originalDirectPrice"
                  type="number"
                  value={originalDirectPrice}
                  onChange={(e) => setOriginalDirectPrice(e.target.value)}
                  placeholder="0"
                />
              </div>
            )}

            <div>
              <Label htmlFor="directPrice">
                {enableReducedDirectPrice
                  ? (L.reducedDirectPrice ?? "Sänkt pris *")
                  : L.directPurchasePrice}
              </Label>
              <p className="text-xs text-muted-foreground mb-1">
                {enableReducedDirectPrice
                  ? (L.reducedDirectPriceDesc ??
                    "Nuvarande försäljningspris som visas för köpare")
                  : L.directPurchasePriceDesc}
              </p>
              <Input
                id="directPrice"
                type="number"
                value={directPrice}
                onChange={(e) => setDirectPrice(e.target.value)}
                placeholder="0"
              />
            </div>

            <div>
              <Label htmlFor="directPurchaseDeliveryFeeZoneA">
                {L.deliveryFeeZoneA}
              </Label>
              <p className="text-xs text-muted-foreground mb-1">
                {L.deliveryFeeZoneADesc}
              </p>
              <Input
                id="directPurchaseDeliveryFeeZoneA"
                type="number"
                value={directPurchaseDeliveryFeeZoneA}
                onChange={(e) => {
                  const value = e.target.value;
                  setDirectPurchaseDeliveryFeeZoneA(value);
                  if (value && !showDirectPurchaseDeliveryZones) {
                    setShowDirectPurchaseDeliveryZones(true);
                  }
                  // Auto-calculate other zones whenever zone A changes
                  if (value) {
                    const zoneA = parseFloat(value);
                    setDirectPurchaseDeliveryFeeZoneB(
                      (zoneA + 1000).toString(),
                    );
                    setDirectPurchaseDeliveryFeeZoneC(
                      (zoneA + 2000).toString(),
                    );
                  }
                }}
                placeholder="1900"
              />
            </div>

            {showDirectPurchaseDeliveryZones && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                <h5 className="font-medium text-blue-600">
                  {L.deliveryFeesPerZone}
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="directPurchaseDeliveryFeeZoneB">
                      {L.zoneB}
                    </Label>
                    <Input
                      id="directPurchaseDeliveryFeeZoneB"
                      type="number"
                      value={directPurchaseDeliveryFeeZoneB}
                      onChange={(e) =>
                        setDirectPurchaseDeliveryFeeZoneB(e.target.value)
                      }
                      placeholder="2900"
                    />
                  </div>
                  <div>
                    <Label htmlFor="directPurchaseDeliveryFeeZoneC">
                      {L.zoneC}
                    </Label>
                    <Input
                      id="directPurchaseDeliveryFeeZoneC"
                      type="number"
                      value={directPurchaseDeliveryFeeZoneC}
                      onChange={(e) =>
                        setDirectPurchaseDeliveryFeeZoneC(e.target.value)
                      }
                      placeholder="3900"
                    />
                  </div>
                  <div>
                    <Label htmlFor="directPurchaseDeliveryFeeZoneD">
                      {L.zoneD}
                    </Label>

                    <p className="mt-2 text-accent">{L.priceOnRequest}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Transport Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Truck className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-900">
                {L.nextStepTransport}
              </h4>
              <p className="text-sm text-blue-700 mt-1">
                {L.nextStepTransportDesc}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case "vehicle":
        return renderVehicleSelection();
      case "type":
        return renderTypeSelection();
      case "campaign":
        return renderCampaignSelection();
      case "rules":
        return renderRules();
      default:
        return null;
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        nested={isNative}
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{isEditMode ? L.editListing : L.createListing}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">{renderStepContent()}</div>

        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === "vehicle"}
          >
            {commonButtonLabels(language)?.back ?? "Tillbaka"}
          </Button>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                onOpenChange(false);
              }}
            >
              {L.cancel ?? "Avbryt"}
            </Button>

            {step === "rules" && (
              <Button
                onClick={handleSubmit}
                disabled={
                  !canProceed() || isSubmitting || (isEditMode && !hasChanges())
                }
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEditMode ? L.saving : L.creating}
                  </>
                ) : isEditMode ? (
                  L.saveChanges
                ) : (
                  L.createListingButton
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog
      open={showExtendCampaignConfirm}
      onOpenChange={setShowExtendCampaignConfirm}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {L.extendCampaignConfirmTitle ?? "Förläng även kampanjen?"}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">
              {(
                L.extendCampaignConfirmDesc ??
                "Annonsens sluttid ({listingEnd}) är efter kampanjens nuvarande slut ({campaignEnd}). Om du fortsätter sätts kampanjens slutdatum till samma tid."
              )
                .replace(
                  "{listingEnd}",
                  endTime ? formatCampaignDateTime(endTime) : "—",
                )
                .replace(
                  "{campaignEnd}",
                  selectedCampaign
                    ? formatCampaignDateTime(selectedCampaign.endDate)
                    : "—",
                )}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>
            {L.cancel ?? btn.cancel ?? "Avbryt"}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault();
              void persistListing(true);
            }}
          >
            {isSubmitting
              ? (L.saving ?? "Sparar...")
              : (L.extendCampaignConfirmAction ?? "Ja, förläng kampanjen")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
